import * as React from "react";
import { Bluelagoon } from "@bluelagoon";
import axios from "axios";
import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "./card";
import { Button } from "./button";
import {
    ZoomIn,
    ZoomOut,
    ChevronLeft,
    ChevronRight,
    Trash2,
} from "lucide-react";

// Simple cn function if @utils is not available
const cn = (...classes: (string | undefined | null | false)[]) => {
    return classes.filter(Boolean).join(" ");
};

export interface Document {
    id: string;
    title: string;
    filename: string;
    url?: string;
}

export interface Participant {
    id: number;
    firstName: string;
    lastName?: string;
}

export interface SignatureElement {
    id: number;
    elementType: string;
    participantId: number;
    xPosition: number;
    yPosition: number;
    width: number;
    height: number;
    pageNumber: number;
}

export interface PdfViewerProps {
    document: Document;
    participants: Participant[];
    signatureElements: SignatureElement[];
    onElementDrop: (
        elementType: string,
        participantId: number,
        x: number,
        y: number,
        pageNumber: number
    ) => void;
    onElementMove?: (
        elementId: number,
        x: number,
        y: number,
        pageNumber: number
    ) => void;
    onElementDelete?: (elementId: number) => void;
    className?: string;
    config?: {
        apiKey?: string;
    };
}

export const PdfViewer = React.forwardRef<HTMLDivElement, PdfViewerProps>(
    (
        {
            document,
            participants,
            signatureElements,
            onElementDrop,
            onElementMove,
            onElementDelete,
            className,
            config,
        },
        ref
    ) => {
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const overlayRef = useRef<HTMLDivElement>(null);
        const [currentPage, setCurrentPage] = useState(1);
        const [totalPages, setTotalPages] = useState(1);
        const [zoom, setZoom] = useState(100);
        const didLoad = useRef<string | undefined>(undefined);
        const [pdfDoc, setPdfDoc] = useState<any>(null);
        const [draggedElement, setDraggedElement] =
            useState<SignatureElement | null>(null);

        useEffect(() => {
            if (didLoad.current !== document?.url && document?.url) {
                didLoad.current = document.url;
                loadPdf();
            }
        }, [document]);

        useEffect(() => {
            if (pdfDoc && currentPage) {
                renderPage(currentPage);
            }
        }, [pdfDoc, currentPage, zoom]);

        const loadPdf = async () => {
            try {
                // Load PDF.js
                const pdfjsLib = (window as any).pdfjsLib;
                if (!pdfjsLib) {
                    console.error("PDF.js not loaded");
                    return;
                }

                pdfjsLib.GlobalWorkerOptions.workerSrc =
                    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

                // Check if config is defined before using it
                if (config) {
                    // use the file url from the document

                    const response = await axios({
                        method: "GET",
                        url: `${Bluelagoon.apiUrl}/proxy/files/signed-url`,
                        headers: {
                            Authorization: `Bearer ${Bluelagoon.getToken()}`,
                            "x-api-key": config.apiKey,
                        },
                        params: {
                            fileUrl: document.url,
                        },
                    });

                    const signedUrl = response.data.url;

                    const loadingTask = pdfjsLib.getDocument(signedUrl);
                    const pdf = await loadingTask.promise;

                    setPdfDoc(pdf);
                    setTotalPages(pdf.numPages);
                    setCurrentPage(1);
                } else {
                    // Fallback when config is not defined
                    console.warn(
                        "Config not defined, using fallback PDF loading"
                    );
                    const loadingTask = pdfjsLib.getDocument(document.url);
                    const pdf = await loadingTask.promise;

                    setPdfDoc(pdf);
                    setTotalPages(pdf.numPages);
                    setCurrentPage(1);
                }
            } catch (error) {
                console.error("Error loading PDF:", error);
                // Fallback to mock PDF rendering
                renderMockPdf();
            }
        };

        const renderPage = async (pageNumber: number) => {
            if (!pdfDoc || !canvasRef.current) return;

            try {
                const page = await pdfDoc.getPage(pageNumber);
                const canvas = canvasRef.current;
                const context = canvas.getContext("2d");

                const viewport = page.getViewport({ scale: zoom / 100 });
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                };

                await page.render(renderContext).promise;
            } catch (error) {
                console.error("Error rendering page:", error);
                renderMockPdf();
            }
        };

        const renderMockPdf = () => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            // Set canvas size
            canvas.width = 612 * (zoom / 100);
            canvas.height = 792 * (zoom / 100);

            // Draw mock PDF content
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.fillStyle = "#333333";
            const scaleFactor = zoom / 100;

            ctx.font = `${16 * scaleFactor}px Inter`;
            ctx.fillText(
                "CONTRACT AGREEMENT",
                50 * scaleFactor,
                50 * scaleFactor
            );

            ctx.font = `${12 * scaleFactor}px Inter`;
            ctx.fillText(
                "This agreement is made between the parties listed below:",
                50 * scaleFactor,
                100 * scaleFactor
            );
            ctx.fillText(
                "1. Party A: _______________________",
                50 * scaleFactor,
                140 * scaleFactor
            );
            ctx.fillText(
                "2. Party B: _______________________",
                50 * scaleFactor,
                160 * scaleFactor
            );

            ctx.fillText(
                "Terms and Conditions:",
                50 * scaleFactor,
                220 * scaleFactor
            );
            ctx.fillText(
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
                50 * scaleFactor,
                260 * scaleFactor
            );
            ctx.fillText(
                "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
                50 * scaleFactor,
                280 * scaleFactor
            );
            ctx.fillText(
                "Ut enim ad minim veniam, quis nostrud exercitation ullamco.",
                50 * scaleFactor,
                300 * scaleFactor
            );

            ctx.fillText("Signatures:", 50 * scaleFactor, 380 * scaleFactor);
            ctx.fillText(
                "Party A Signature: _____________________ Date: _________",
                50 * scaleFactor,
                420 * scaleFactor
            );
            ctx.fillText(
                "Party B Signature: _____________________ Date: _________",
                50 * scaleFactor,
                480 * scaleFactor
            );

            ctx.fillText("Witness:", 50 * scaleFactor, 560 * scaleFactor);
            ctx.fillText(
                "Witness Signature: _____________________ Date: _________",
                50 * scaleFactor,
                580 * scaleFactor
            );
            ctx.fillText(
                "Witness Initial: ______",
                50 * scaleFactor,
                620 * scaleFactor
            );
        };

        const handleDragOver = (e: React.DragEvent) => {
            e.preventDefault();
        };

        const handleDrop = (e: React.DragEvent) => {
            e.preventDefault();

            const elementType = e.dataTransfer.getData("text/plain");
            const participantIdStr = e.dataTransfer.getData("participant-id");
            const draggedElementId =
                e.dataTransfer.getData("dragged-element-id");
            const offsetX = parseFloat(e.dataTransfer.getData("offset-x")) || 0;
            const offsetY = parseFloat(e.dataTransfer.getData("offset-y")) || 0;

            if (!overlayRef.current) return;

            const rect = overlayRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left - offsetX;
            const y = e.clientY - rect.top - offsetY;

            // If we're moving an existing element
            if (draggedElementId) {
                const elementId = parseInt(draggedElementId);
                if (onElementMove) {
                    onElementMove(elementId, x, y, currentPage);
                }
            }
            // If we're dropping a new element
            else if (elementType && participantIdStr) {
                const participantId = parseInt(participantIdStr);
                onElementDrop(elementType, participantId, x, y, currentPage);
            }
        };

        const handleElementDragStart = (
            e: React.DragEvent,
            element: SignatureElement
        ) => {
            setDraggedElement(element);
            e.dataTransfer.setData("dragged-element-id", element.id.toString());
            e.dataTransfer.effectAllowed = "move";
        };

        const getParticipantColor = (participantId: number): string => {
            const colors = ["blue", "green", "purple", "red", "orange", "pink"];
            const index = participantId % colors.length;
            return colors[index];
        };

        const getParticipantById = (id: number) => {
            return participants.find((p) => p.id === id);
        };

        const currentPageElements = signatureElements.filter(
            (el) => el.pageNumber === currentPage
        );

        return (
            <Card ref={ref} className={cn("flex-1", className)}>
                <CardContent className="p-0">
                    {/* Document Header */}
                    <div className="border-b border-gray-200 p-4 flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {document.title}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {document.filename}
                            </p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setZoom(Math.max(50, zoom - 25))}
                                disabled={zoom <= 50}
                            >
                                <ZoomOut className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-gray-700 min-w-[50px] text-center">
                                {zoom}%
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    setZoom(Math.min(200, zoom + 25))
                                }
                                disabled={zoom >= 200}
                            >
                                <ZoomIn className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* PDF Content */}
                    <div className="p-6 overflow-auto max-h-screen">
                        <div
                            className="relative mx-auto bg-white shadow-lg"
                            style={{ width: `${612 * (zoom / 100)}px` }}
                        >
                            <canvas
                                ref={canvasRef}
                                className="w-full border border-gray-300"
                            />

                            {/* Overlay for signature elements */}
                            <div
                                ref={overlayRef}
                                className="absolute inset-0 pointer-events-none"
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                style={{ pointerEvents: "auto" }}
                            >
                                {currentPageElements.map((element) => {
                                    const participant = getParticipantById(
                                        element.participantId || 0
                                    );
                                    const color = getParticipantColor(
                                        element.participantId || 0
                                    );

                                    return (
                                        <div
                                            key={element.id}
                                            draggable
                                            onDragStart={(e) =>
                                                handleElementDragStart(
                                                    e,
                                                    element
                                                )
                                            }
                                            className={`absolute border-2 border-dashed pointer-events-auto cursor-move bg-${color}-50 border-${color}-500 group hover:border-${color}-600`}
                                            style={{
                                                left: `${element.xPosition}px`,
                                                top: `${element.yPosition}px`,
                                                width: `${element.width}px`,
                                                height: `${element.height}px`,
                                            }}
                                        >
                                            <div
                                                className={`flex items-center justify-center h-full text-xs text-${color}-600 font-medium relative`}
                                            >
                                                {participant &&
                                                    `${participant.firstName} - ${element.elementType}`}

                                                {/* Delete button */}
                                                {onElementDelete && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onElementDelete(
                                                                element.id
                                                            );
                                                        }}
                                                        className={`absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}
                                                        title="Delete element"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Page Navigation */}
                        <div className="flex justify-center items-center mt-4 space-x-4">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    setCurrentPage(Math.max(1, currentPage - 1))
                                }
                                disabled={currentPage <= 1}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-gray-700">
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    setCurrentPage(
                                        Math.min(totalPages, currentPage + 1)
                                    )
                                }
                                disabled={currentPage >= totalPages}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }
);

PdfViewer.displayName = "PdfViewer";
